import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { PenLine, Loader2, Lock, Eye, Coins, History } from 'lucide-react';
import QrcVerdictView from './QrcVerdictView';
import QrcAttemptModal from './QrcAttemptModal';
import QrcPackCheckoutModal from '../stripe/QrcPackCheckoutModal';
import useQrcGrading, { newRequestKey } from './useQrcGrading';

/** Clé d'idempotence (spec crédits §4.1) : définie dans useQrcGrading, ré-exportée ici. */
export { newRequestKey };

/**
 * Forçage du mode crédits par un administrateur (spec §8, v0) : un drapeau
 * local, lu par le player et l'audit. Sans effet pour un non-admin (le serveur
 * n'honore `policy=credits` que pour un admin).
 */
export const QRC_FORCE_KEY = 'monquiz_qrc_force_credits';
export function qrcForceCredits() {
    try { return localStorage.getItem(QRC_FORCE_KEY) === '1'; } catch { return false; }
}

/**
 * Vitrine d'exemples pré-corrigés d'une QRC démo : onglets réponse → verdict figé.
 * `isPreview` = rendu de contrôle admin (« vue non-abonné », sans CTA abonnement).
 */
export function QrcShowcase({ q, isPreview = false, noCta = false }) {
    // La réponse à retravailler d’abord (non acquise, score le plus bas), la plus complète
    // sur un clic (demande Fabien, 29/09/2026) — quel que soit l'ordre de publication.
    const samples = useMemo(() => [...(q.samples || [])].sort((a, b) => {
        const pa = Number(!!(a.verdict?.passed)), pb = Number(!!(b.verdict?.passed));
        if (pa !== pb) return pa - pb;
        return (a.verdict?.score_core_pct ?? a.verdict?.score_pct ?? 0) - (b.verdict?.score_core_pct ?? b.verdict?.score_pct ?? 0);
    }), [q.samples]);
    const [idx, setIdx] = useState(0);
    return (
        <>
            <p className="qrcp-showcase-intro">
                {isPreview
                    ? <>Ce que voit un visiteur sans abonnement : {samples.length} réponse{samples.length > 1 ? 's' : ''} d'exemple, chacune avec le verdict du juge figé à la publication.</>
                    : <>Découvre comment le correcteur évalue de vraies réponses — chaque exemple ci-dessous a été corrigé par le moteur{samples.length > 1 ? ', la réponse à retravailler d’abord' : ''}.</>}
            </p>
            <div className="qrcp-showcase-tabs">
                {samples.map((s, i) => (
                    <button
                        key={i} type="button"
                        className={i === idx ? 'is-on' : ''}
                        onClick={() => setIdx(i)}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
            <div className="qrcp-showcase-answer">
                <span>Réponse proposée</span>
                <p>{samples[idx]?.answer_text}</p>
            </div>
            {samples[idx]?.verdict && <QrcVerdictView verdict={samples[idx].verdict} />}
            {!isPreview && !noCta && (
                <p className="qrcp-showcase-cta">
                    <Lock size={12} /> Avec un abonnement, tu rédiges ta propre réponse et le correcteur te rend <strong>ton</strong> verdict personnalisé.
                </p>
            )}
        </>
    );
}

/**
 * Réponse rédigée d'un sous-sujet (QRC corrigée par IA).
 * Ne s'affiche que si le sous-sujet a des QRC. Autonome : liste → réponse → verdict.
 * Non-abonné : la QRC démo affiche une VITRINE d'exemples pré-corrigés
 * (samples figés à la publication) — le moteur reste réservé aux abonnés.
 * Admin : reçoit aussi les samples → bloc repliable « vue non-abonné » + badge Brouillon.
 *
 * `scope` = slug de la thématique depuis laquelle on joue (spec crédits §2.1) :
 * c'est lui qui fixe la politique (inclus / crédits) et le droit d'accès.
 * En mode crédits, la réponse porte un solde, et chaque soumission emporte une
 * clé d'idempotence : une relance après coupure ne débite jamais deux fois.
 */
export default function QrcPlayer({ sousSujetId, title = 'Réponse rédigée', scope = null, onOpenSujet = null, onCreditsChange = null }) {
    const [list, setList] = useState(null); // null = chargement, [] = aucune
    const [meta, setMeta] = useState({});   // policy, credits, is_admin, credits_available
    const [openId, setOpenId] = useState(null);
    const [drafts, setDrafts] = useState({}); // brouillon par question : rien ne s'efface en changeant de question
    const [force, setForce] = useState(qrcForceCredits());
    const [viewAttemptId, setViewAttemptId] = useState(null); // relecture d'une correction passée
    const [buyOpen, setBuyOpen] = useState(false); // achat d'un pack de corrections

    const load = useCallback(() => {
        const params = new URLSearchParams({ sous_sujet_id: String(sousSujetId) });
        if (scope) params.set('scope_slug', scope);
        if (force) params.set('policy', 'credits');
        api.get(`/qcm/qrc-list.php?${params}`)
            .then(({ data }) => {
                const d = data.data || {};
                setList(d.qrc || []);
                setMeta({ policy: d.policy, credits: d.credits, is_admin: !!d.is_admin, credits_available: !!d.credits_available });
            })
            .catch(() => setList([]));
    }, [sousSujetId, scope, force]);
    useEffect(() => { load(); }, [load]);
    // Soumission + clé d'idempotence (partagées avec l'atelier QrcWorkshop)
    const grader = useQrcGrading({
        scope, force,
        onCredits: (c) => setMeta(m => ({ ...m, credits: c })),
        onGraded: () => load(), // rafraîchit l'état (tentée / rejouable / solde)
    });
    // Le solde change à chaque correction (et à chaque refus faute de crédit) : le parent
    // qui affiche aussi le solde (bloc de démonstration) est tenu au courant.
    useEffect(() => { if (onCreditsChange && meta.credits) onCreditsChange(meta.credits); }, [meta.credits]); // eslint-disable-line react-hooks/exhaustive-deps

    if (list === null || list.length === 0) return null;

    const creditsMode = meta.policy === 'credits';
    const balance = meta.credits?.balance ?? null;

    const open = (q) => setOpenId(q.id); // brouillon et verdict de chaque question conservés (revue Astra, 29/09/2026)

    const toggleForce = (on) => {
        try { on ? localStorage.setItem(QRC_FORCE_KEY, '1') : localStorage.removeItem(QRC_FORCE_KEY); } catch { /* stockage indisponible */ }
        setForce(on);
    };


    return (
        <div className="qrcp">
            <div className="qrcp-head">
                <PenLine size={15} /> {title} <span className="qrcp-count">{list.length}</span>
                {creditsMode && balance !== null && (
                    <span className={`qrcp-badge ${balance > 0 ? 'ok' : 'ko'}`} title="Ton solde de corrections : versé par chaque abonnement selon sa durée, jamais perdu">
                        <Coins size={11} /> Solde : {balance} correction{balance > 1 ? 's' : ''}
                    </span>
                )}
                {creditsMode && balance !== null && (
                    <button type="button" className="qrcp-buy-link" onClick={() => setBuyOpen(true)}>Recharger</button>
                )}
            </div>
            <p className="qrcp-intro">Réponds en quelques phrases : une correction détaillée t'indique ce qu'il te reste à travailler.</p>
            {!!Number(meta.is_admin) && !!Number(meta.credits_available) && (
                <label className="qrcp-admin-force" title="v0 : le mode crédits n'est visible que des administrateurs (spec §8)">
                    <input type="checkbox" checked={force} onChange={e => toggleForce(e.target.checked)} />
                    {' '}Admin — forcer le mode crédits sur cette session
                </label>
            )}
            {list.map(q => {
                const isOpen = openId === q.id;
                const locked = q.attempted && !q.replayable;
                const noCredit = creditsMode && balance !== null && balance <= 0;
                // Vitrine : QRC démo pour un non-abonné avec exemples figés
                const showcase = !q.accessible && q.is_demo && Array.isArray(q.samples) && q.samples.length > 0;
                const st = grader.get(q.id); // état de correction propre à CETTE question
                const answer = drafts[q.id] || '';
                const setAnswer = (v) => setDrafts(d => ({ ...d, [q.id]: v }));
                return (
                    <div key={q.id} className={`qrcp-item ${isOpen ? 'is-open' : ''}`}>
                        <button
                            type="button" className="qrcp-item-head"
                            onClick={() => (isOpen ? setOpenId(null) : open(q))}
                            disabled={!q.accessible && !showcase}
                        >
                            <span className="qrcp-item-title">{q.enonce}</span>
                            <span className="qrcp-item-state">
                                {q.status === 'EDITION' && <span className="qrcp-badge draft">Brouillon</span>}
                                {!!Number(q.is_demo) && !!Number(q.accessible) && <span className="qrcp-demo">démo</span>}
                                {showcase ? <span className="qrcp-badge new"><Eye size={11} /> Exemples corrigés</span>
                                    : !q.accessible ? <span className="qrcp-badge lock"><Lock size={11} /> Abonnement</span>
                                        : st.grading ? <span className="qrcp-badge new"><Loader2 size={11} className="spin" /> Correction…</span>
                                            : q.attempted ? <span className={`qrcp-badge ${q.last_passed ? 'ok' : 'ko'}`}>{q.last_score} %</span>
                                                : <span className="qrcp-badge new">À faire</span>}
                            </span>
                        </button>
                        {isOpen && showcase && (
                            <div className="qrcp-body">
                                <QrcShowcase key={q.id} q={q} />
                            </div>
                        )}
                        {isOpen && q.accessible && (
                            <div className="qrcp-body">
                                {/* Contrôle admin : la vitrine telle que la voit un non-abonné
                                    (les samples ne sont envoyés qu'aux non-abonnés et aux admins) */}
                                {Array.isArray(q.samples) && q.samples.length > 0 && (
                                    <details className="qrcp-admin-vitrine">
                                        <summary><Eye size={13} /> Vitrine démo — vue non-abonné ({q.samples.length} exemple{q.samples.length > 1 ? 's' : ''})</summary>
                                        <div className="qrcp-admin-vitrine-body">
                                            <QrcShowcase key={q.id} q={q} isPreview />
                                        </div>
                                    </details>
                                )}
                                {!st.verdict ? (
                                    <>
                                        {locked && <p className="qrcp-locked"><Lock size={12} /> {q.blocked_reason}</p>}
                                        {noCredit && !locked && (
                                            <p className="qrcp-locked">
                                                <Coins size={12} /> Ton solde de corrections est à zéro : ton prochain abonnement en ajoutera, ou recharge dès 3 € (ce que tu n'utilises pas ne se perd jamais).
                                                {' '}<button type="button" className="qrcp-buy-link qrcp-buy-link--inline" onClick={() => setBuyOpen(true)}>Recharger des corrections</button>
                                            </p>
                                        )}
                                        {q.attempted && q.last_attempt_id && (
                                            <button type="button" className="qrcp-lastlink" onClick={() => setViewAttemptId(q.last_attempt_id)}>
                                                <History size={12} /> Voir ma dernière correction ({q.last_score} %)
                                            </button>
                                        )}
                                        <textarea
                                            rows={5} value={answer} maxLength={5000}
                                            onChange={e => { setAnswer(e.target.value); grader.touch(q.id); }}
                                            placeholder="Rédige ta réponse en quelques phrases…"
                                            disabled={locked || noCredit}
                                        />
                                        {st.err && <p className="qrcp-err">{st.err}</p>}
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => grader.submit(q, answer, st.pendingKey)}
                                            disabled={st.grading || !answer.trim() || locked || noCredit}
                                        >
                                            {st.grading ? <><Loader2 size={14} className="spin" /> Correction…</>
                                                : st.pendingKey ? 'Récupérer ma correction'
                                                    : creditsMode ? 'Corriger ma réponse (1 correction)' : 'Corriger ma réponse'}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        {st.answer && (
                                            <details className="qrcp-sent">
                                                <summary><PenLine size={12} /> Ta réponse</summary>
                                                <p>{st.answer}</p>
                                            </details>
                                        )}
                                        <QrcVerdictView verdict={st.verdict} onReplay={() => { grader.reset(q.id); setAnswer(''); }} />
                                        {/* Suite utile après la correction : le quiz du sujet lié (raccourci, sans conditionner le rejeu) */}
                                        {onOpenSujet && (
                                            <div className="qrcp-next">
                                                <span className="qrcp-next-k">Pour progresser</span>
                                                {/* Chapitres à travailler (migration 146) ; à défaut, le sujet d'appartenance */}
                                                {(q.revisions?.length ? q.revisions : [{ id: sousSujetId, label: title }]).map(r => (
                                                    <button key={r.id} type="button" className="qrcp-next-chip" onClick={() => onOpenSujet(r.id)}>Quiz · {r.label}</button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
            {viewAttemptId && <QrcAttemptModal attemptId={viewAttemptId} onClose={() => setViewAttemptId(null)} />}
            {buyOpen && <QrcPackCheckoutModal onClose={() => setBuyOpen(false)} onSuccess={() => load()} />}
        </div>
    );
}
